import { useMutation, useQuery } from "@apollo/client";

import {
  CREATE_FINANCE_QUOTE_MUTATION,
  GET_FINANCE_QUOTE_QUERY,
  LIST_FINANCE_QUOTES_QUERY,
  UPDATE_FINANCE_QUOTE_MUTATION,
} from "~/api";
import { ITEMS_PER_PAGE } from "~/constants";
import { defaultQuoteLineItemsOnCreateList } from "~/features/Quotes/detail/utils/lineItems";
import type { FinanceQuote, FinanceQuotesListResult } from "~/types/finance";
import { errorsHandler } from "~/transformers";
import { buildListFinanceQuotesVariables } from "~/utils/finance/buildFinanceQuotesQuery";

export const useListFinanceQuotes = ({
  activeFilters,
  searchQuery,
  limit = ITEMS_PER_PAGE,
  skip = 0,
  sort = ["created_at:desc"],
  skipCondition = false,
}: {
  activeFilters?: Record<string, unknown>;
  searchQuery?: string;
  limit?: number;
  skip?: number;
  sort?: string[];
  skipCondition?: boolean;
} = {}) => {
  const variables = buildListFinanceQuotesVariables({
    activeFilters,
    searchQuery,
    limit,
    skip,
    sort,
  });

  const { data, loading, error, refetch, fetchMore } = useQuery<{
    listFinanceQuotes: FinanceQuotesListResult;
  }>(LIST_FINANCE_QUOTES_QUERY, {
    variables,
    skip: skipCondition,
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
  });

  const result = data?.listFinanceQuotes;

  const handleFetchMore = ({
    limit: moreLimit,
    skip: moreSkip,
  }: {
    limit: number;
    skip: number;
  }) => {
    const moreVariables = buildListFinanceQuotesVariables({
      activeFilters,
      searchQuery,
      limit: moreLimit,
      skip: moreSkip,
      sort,
    });
    return fetchMore({
      variables: moreVariables,
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult?.listFinanceQuotes) return prev;
        return {
          listFinanceQuotes: {
            ...fetchMoreResult.listFinanceQuotes,
            quotes: [
              ...(prev.listFinanceQuotes?.quotes ?? []),
              ...fetchMoreResult.listFinanceQuotes.quotes,
            ],
          },
        };
      },
    });
  };

  return {
    quotes: result?.quotes ?? [],
    totalCount: result?.totalCount ?? 0,
    loading,
    error,
    refetch,
    handleFetchMore,
  };
};

export const useCreateFinanceQuote = () => {
  const [mutate, { loading }] = useMutation(CREATE_FINANCE_QUOTE_MUTATION);
  const [updateMutate] = useMutation(UPDATE_FINANCE_QUOTE_MUTATION);

  return {
    createFinanceQuote: (input: { company: string; workOrder?: string }) =>
      new Promise<FinanceQuote>(async (resolve, reject) => {
        try {
          const { data } = await mutate({
            variables: { input },
            refetchQueries: [{ query: LIST_FINANCE_QUOTES_QUERY }],
          });
          const quote = data?.createFinanceQuote as
            | (FinanceQuote & { lineItems?: Array<{ _id?: string }> })
            | undefined;
          // Keep WO-seeded rows; add one empty line item when create returns none.
          if (quote?._id && !quote.lineItems?.length) {
            await updateMutate({
              variables: {
                input: {
                  _id: quote._id,
                  lineItems: defaultQuoteLineItemsOnCreateList(),
                },
              },
              refetchQueries: [
                {
                  query: GET_FINANCE_QUOTE_QUERY,
                  variables: { id: quote._id },
                },
                { query: LIST_FINANCE_QUOTES_QUERY },
              ],
            });
          }
          resolve(quote);
        } catch (err) {
          reject(errorsHandler(err));
        }
      }),
    loading,
  };
};
